// frontend/src/hooks/bookingManagement/useBookingValidation.js


/**
 * useBookingValidation Hook
 *
 * Custom React hook that encapsulates all booking validation logic.
 * Extracted from BookingManagement.js to improve code organization and reusability.
 *
 * @module hooks/bookingManagement/useBookingValidation
 *
 * @description
 * This hook provides a comprehensive validation function for booking forms.
 * It validates all required fields, enforces business rules, and provides
 * user-friendly error messages via toast notifications.
 *
 * Validation includes:
 * - Admin-specific validations (user selection)
 * - Date and time selection validation
 * - Required field validation (route, pickup, dropoff, riders, contact, luggage)
 * - Contact number format validation
 * - Luggage limit enforcement (per-rider calculation)
 * - Route sequence validation (pickup before dropoff)
 * - Entity verification field validation
 *
 * @example
 * const { validateBooking } = useBookingValidation({
 *   booking,
 *   routes,
 *   t,
 *   isAdminBooking,
 *   selectedUserId,
 *   canBookRecurring,
 *   bookAllAvailableDates,
 *   availableDates,
 *   selectedDates,
 *   maximumLuggagePerRider,
 *   maximumLuggageEnforced,
 *   additionalVerificationEnabled,
 *   setVerificationError
 * });
 *
 * if (!validateBooking()) {
 *   return; // Validation failed, error shown via toast
 * }
 * // Proceed with booking submission...
 */

import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { haveSameLocationAddress } from '../../utils/bookingLocationUtils';

/**
 * Validates a contact number using a phone number regex pattern
 *
 * Empty/blank values are considered valid (optional field).
 * Supports international formats like +86 1391 042 9758.
 *
 * @param {string} number - The contact number to validate
 * @returns {boolean} True if the number is valid or empty, false otherwise
 *
 * @private
 */
const validateContactNumber = (number) => {
  // Allow empty/blank values (optional field)
  if (!number || number.trim() === '') {
    return true;
  }

  // Regex: Accepts optional '+' prefix, followed by digits/spaces/hyphens (min 10 chars)
  const phoneRegex = /^\+?[\d\s-]{10,}$/;
  return phoneRegex.test(number);
};

/**
 * Custom hook for booking validation
 *
 * @param {Object} params - Validation parameters
 * @param {Object} params.booking - The booking object containing form data
 * @param {string} params.booking.route - Selected route ID
 * @param {string} params.booking.pickup - Selected pickup location ID
 * @param {string} params.booking.dropoff - Selected dropoff location ID
 * @param {Date|null} params.booking.date - Selected date (for single booking)
 * @param {string} params.booking.schedule - Selected time slot
 * @param {string} params.booking.riders - Number of riders (string from input)
 * @param {string} params.booking.luggage - Number of luggage pieces (string from input)
 * @param {string} params.booking.contactNumber - Contact phone number
 * @param {string} params.booking.entityVerificationValue - Additional verification value
 *
 * @param {Array<Object>} params.routes - Available routes with waypoint data
 * @param {Function} params.t - i18n translation function
 *
 * @param {boolean} params.isAdminBooking - Whether this is an admin creating a booking for a user
 * @param {number|null} params.selectedUserId - ID of the selected user (required for admin bookings)
 *
 * @param {boolean} params.canBookRecurring - Whether recurring bookings are enabled
 * @param {boolean} params.bookAllAvailableDates - Whether user selected "all available dates" option
 * @param {Array<string>} params.availableDates - Array of available dates (YYYY-MM-DD format)
 * @param {Array<string>} params.selectedDates - Array of selected dates for recurring bookings
 *
 * @param {number} params.maximumLuggagePerRider - Max luggage pieces per rider (0 = field hidden)
 * @param {boolean} params.maximumLuggageEnforced - Whether to block bookings exceeding luggage limit
 *
 * @param {boolean} params.additionalVerificationEnabled - Whether entity verification field is required
 * @param {Function} params.setVerificationError - State setter for verification error messages
 *
 * @returns {Object} Object containing the validateBooking function
 * @returns {Function} returns.validateBooking - Validation function that returns boolean
 */
const useBookingValidation = ({
  booking,
  routes,
  t,
  isAdminBooking = false,
  selectedUserId = null,
  canBookRecurring = false,
  bookAllAvailableDates = false,
  availableDates = [],
  selectedDates = [],
  maximumLuggagePerRider = 0,
  maximumLuggageEnforced = false,
  additionalVerificationEnabled = false,
  setVerificationError = () => {}
}) => {
  /**
   * Validates the entire booking form
   *
   * This function performs comprehensive validation of all booking fields and business rules.
   * It shows user-friendly error messages via toast notifications.
   *
   * @returns {boolean} True if all validations pass, false otherwise
   *
   * @description
   * Validation order:
   * 1. Admin user selection (if admin booking)
   * 2. Date selection (single or multiple dates)
   * 3. Time slot selection
   * 4. Required fields (route, pickup, dropoff, riders, luggage, contact)
   * 5. Contact number format
   * 6. Number of riders validation
   * 7. Luggage validation (if enabled)
   * 8. Route sequence validation (pickup before dropoff)
   * 9. Entity verification field validation (if enabled)
   */
  const validateBooking = useCallback(() => {
    // Define required fields for validation
    // Note: luggage is conditionally added below if the field is visible
    const requiredFields = [
      { name: 'riders', label: t('Number of Riders') },
      { name: 'route', label: t('Route') },
      // { name: 'contactNumber', label: t('Contact Number') }, this is now optional
      { name: 'pickup', label: t('Pickup Location') },
      { name: 'dropoff', label: t('Dropoff Location') }
    ];

    // ===================================================================
    // STEP 1: Validate admin user selection (admin bookings only)
    // ===================================================================
    if (isAdminBooking && !selectedUserId) {
      toast.error(t('selectUserToCreateBooking'));
      return false;
    }

    // ===================================================================
    // STEP 2: Add luggage to required fields if the field is visible
    // ===================================================================
    // Luggage field is only shown when maximumLuggagePerRider > 0
    if (maximumLuggagePerRider > 0) {
      requiredFields.push({ name: 'luggage', label: t('Pieces of Luggage') });
    }

    // ===================================================================
    // STEP 3: Validate date selection
    // ===================================================================
    if (canBookRecurring) {
      // For recurring bookings, validate based on booking mode
      if (bookAllAvailableDates) {
        // "Book all available dates" mode: must have available dates
        if (!availableDates.length) {
          toast.error(t('No available dates for booking'));
          return false;
        }
      } else {
        // Multiple dates mode: must have selected at least one date
        if (!selectedDates.length) {
          toast.error(t('Please select at least one date'));
          return false;
        }
      }
    } else {
      // Single booking mode: must have a date selected
      if (!booking.date) {
        toast.error(t('Date is required'));
        return false;
      }
    }

    // ===================================================================
    // STEP 4: Validate time slot selection
    // ===================================================================
    if (!booking.schedule) {
      toast.error(t('Time is required'));
      return false;
    }

    // ===================================================================
    // STEP 5: Validate all required fields
    // ===================================================================
    for (const field of requiredFields) {
      if (!booking[field.name]) {
        toast.error(`${field.label} ${t('is required')}`);
        return false;
      }
    }

    // ===================================================================
    // STEP 6: Validate contact number format
    // ===================================================================
    if (!validateContactNumber(booking.contactNumber)) {
      toast.error(t('Please enter a valid contact number'));
      return false;
    }

    // ===================================================================
    // STEP 7: Validate number of riders
    // ===================================================================
    const requestedRiders = parseInt(booking.riders);
    if (isNaN(requestedRiders) || requestedRiders < 1) {
      toast.error(t('Please enter a valid number of riders'));
      return false;
    }

    // ===================================================================
    // STEP 8: Validate luggage (if field is visible)
    // ===================================================================
    // Only validate luggage if maximumLuggagePerRider > 0 (field is shown)
    if (maximumLuggagePerRider > 0) {
      const requestedLuggage = parseInt(booking.luggage);

      // Validate luggage is a valid number
      if (isNaN(requestedLuggage)) {
        toast.error(t('luggageInvalidNumber'));
        return false;
      }

      // Validate luggage is not negative
      if (requestedLuggage < 0) {
        toast.error(t('luggageCannotBeNegative'));
        return false;
      }

      // Calculate total allowed luggage based on riders
      // Formula: totalAllowed = numberOfRiders × maxLuggagePerRider
      const totalAllowedLuggage = requestedRiders * maximumLuggagePerRider;

      // Check if requested luggage exceeds the per-rider limit
      if (requestedLuggage > totalAllowedLuggage) {
        if (maximumLuggageEnforced) {
          // Hard limit: Block the booking if enforcement is enabled
          toast.error(t('maximumLuggagePerRiderExceeded', {
            maxPerRider: maximumLuggagePerRider,
            riders: requestedRiders,
            totalAllowed: totalAllowedLuggage
          }));
          return false;
        } else {
          // Soft limit: Show warning but allow booking to proceed
          toast.warning(t('maximumLuggagePerRiderExceeded', {
            maxPerRider: maximumLuggagePerRider,
            riders: requestedRiders,
            totalAllowed: totalAllowedLuggage
          }));
        }
      }
    }

    // ===================================================================
    // STEP 9: Validate route sequence (pickup before dropoff)
    // ===================================================================
    // Find the current route from the routes array
    const currentRoute = routes.find(route => route.id.toString() === booking.route);

    if (currentRoute) {
      const pickupWaypoint = currentRoute.waypoints?.find(
        (waypoint) => waypoint.id.toString() === booking.pickup
      );
      const dropoffWaypoint = currentRoute.waypoints?.find(
        (waypoint) => waypoint.id.toString() === booking.dropoff
      );

      if (haveSameLocationAddress(pickupWaypoint, dropoffWaypoint)) {
        toast.error(t('pickupAndDropoffAddressesAreSame'));
        return false;
      }

      // Get pickup locations (waypoints where can_pickup = true)
      const pickupLocations = currentRoute.waypoints
        ?.filter(waypoint => waypoint.can_pickup)
        .sort((a, b) => a.sequence_number - b.sequence_number)
        .map(waypoint => ({
          id: waypoint.id,
          sequence_number: waypoint.sequence_number
        })) || [];

      // Get dropoff locations (waypoints where can_dropoff = true)
      const dropoffLocations = currentRoute.waypoints
        ?.filter(waypoint => waypoint.can_dropoff)
        .sort((a, b) => a.sequence_number - b.sequence_number)
        .map(waypoint => ({
          id: waypoint.id,
          sequence_number: waypoint.sequence_number
        })) || [];

      // Find selected pickup and dropoff waypoints
      const pickupLocation = pickupLocations.find(loc => loc.id.toString() === booking.pickup);
      const dropoffLocation = dropoffLocations.find(loc => loc.id.toString() === booking.dropoff);

      // Validate pickup comes before dropoff in route sequence
      if (pickupLocation && dropoffLocation &&
          pickupLocation.sequence_number >= dropoffLocation.sequence_number) {
        toast.error(t('pickupLocationMustBeBeforeDropoffLocation'));
        return false;
      }
    }

    // ===================================================================
    // STEP 10: Validate entity verification field (if enabled)
    // ===================================================================
    const trimmedVerificationValue = booking.entityVerificationValue?.trim() || '';

    if (additionalVerificationEnabled) {
      // Verification field is required and must not be empty
      if (!trimmedVerificationValue) {
        const message = t('entityVerificationValueRequiredMessage');
        setVerificationError(message);
        toast.error(message);
        return false;
      }

      // Check maximum length (database limit: 255 characters)
      if (trimmedVerificationValue.length > 255) {
        const message = t('entityVerificationValueTooLongMessage', { maxLength: 255 });
        setVerificationError(message);
        toast.error(message);
        return false;
      }

      // Clear error if validation passes
      setVerificationError('');
    } else {
      // Verification field is optional, but still enforce max length if provided
      if (trimmedVerificationValue.length > 255) {
        const message = t('entityVerificationValueTooLongMessage', { maxLength: 255 });
        setVerificationError(message);
        toast.error(message);
        return false;
      } else {
        setVerificationError('');
      }
    }

    // ===================================================================
    // All validations passed
    // ===================================================================
    return true;
  }, [
    // Dependencies for useCallback
    booking,
    routes,
    t,
    isAdminBooking,
    selectedUserId,
    canBookRecurring,
    bookAllAvailableDates,
    availableDates,
    selectedDates,
    maximumLuggagePerRider,
    maximumLuggageEnforced,
    additionalVerificationEnabled,
    setVerificationError
  ]);

  return {
    validateBooking
  };
};

export default useBookingValidation;
